'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { handleError } from '@/utils/errorHandler';

interface WebSocketMessage {
  type: string;
  payload?: unknown;
  timestamp?: string;
}

interface UseVenueWebSocketOptions {
  enabled: boolean;
  venueIds?: string[];
  onMessage?: (message: WebSocketMessage) => void;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const INITIAL_RECONNECT_DELAY = 1000; // 1 second

export function useVenueWebSocket(options: UseVenueWebSocketOptions) {
  const { enabled, venueIds = [], onMessage } = options;

  const [connectionState, setConnectionState] =
    useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectDelayRef = useRef<number>(INITIAL_RECONNECT_DELAY);
  const intentionalCloseRef = useRef<boolean>(false);
  const subscribedVenuesRef = useRef<string[]>([]);

  const isConnected = connectionState === 'connected';

  const connect = useCallback(() => {
    if (!enabled) return;

    // Don't create a new connection if one already exists
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    setConnectionState('connecting');

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionState('connected');
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;

        // Don't auto-subscribe here - let the venue subscription effect handle it
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);
          if (onMessage) {
            onMessage(message);
          }
        } catch (error) {
          handleError(error, {
            component: 'useVenueWebSocket',
            action: 'parseMessage',
          });
        }
      };

      ws.onerror = (error) => {
        setConnectionState('error');
        handleError(error, {
          component: 'useVenueWebSocket',
          action: 'connection',
        });
      };

      ws.onclose = () => {
        wsRef.current = null;
        subscribedVenuesRef.current = [];
        setConnectionState('disconnected');

        // Only reconnect if it wasn't an intentional close and we're still enabled
        if (!intentionalCloseRef.current && enabled) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelayRef.current);

          // Exponential backoff: double the delay, up to MAX_RECONNECT_DELAY
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current * 2,
            MAX_RECONNECT_DELAY
          );
        }
      };
    } catch (error) {
      setConnectionState('error');
      handleError(error, {
        component: 'useVenueWebSocket',
        action: 'connect',
      });
    }
  }, [enabled, onMessage]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    subscribedVenuesRef.current = [];
    setConnectionState('disconnected');
  }, []);

  const sendMessage = useCallback(
    (message: WebSocketMessage) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(message));
      } else {
        handleError(new Error('WebSocket is not connected'), {
          component: 'useVenueWebSocket',
          action: 'sendMessage',
        });
      }
    },
    []
  );

  const subscribe = useCallback(
    (newVenueIds: string[]) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: 'subscribe',
            venueIds: newVenueIds,
          })
        );
        subscribedVenuesRef.current = [
          ...new Set([...subscribedVenuesRef.current, ...newVenueIds]),
        ];
      }
    },
    []
  );

  const unsubscribe = useCallback((venueIdsToRemove: string[]) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'unsubscribe',
          venueIds: venueIdsToRemove,
        })
      );
      subscribedVenuesRef.current = subscribedVenuesRef.current.filter(
        (id) => !venueIdsToRemove.includes(id)
      );
    }
  }, []);

  // Effect to handle connection lifecycle
  useEffect(() => {
    if (enabled) {
      intentionalCloseRef.current = false;
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Effect to handle venue subscription changes
  useEffect(() => {
    if (!enabled || !isConnected) return;

    const currentVenues = new Set(subscribedVenuesRef.current);
    const newVenues = new Set(venueIds);

    // Find venues to subscribe to
    const toSubscribe = venueIds.filter((id) => !currentVenues.has(id));
    // Find venues to unsubscribe from
    const toUnsubscribe = subscribedVenuesRef.current.filter(
      (id) => !newVenues.has(id)
    );

    if (toSubscribe.length > 0) {
      subscribe(toSubscribe);
    }

    if (toUnsubscribe.length > 0) {
      unsubscribe(toUnsubscribe);
    }
  }, [enabled, isConnected, venueIds, subscribe, unsubscribe]);

  return {
    connectionState,
    isConnected,
    lastMessage,
    sendMessage,
    subscribe,
    unsubscribe,
    connect,
    disconnect,
  };
}
